import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  sendOtp,
  verifyOtp,
  loginWithPassword,
  sendPasswordRecoveryOtp,
  resetPassword,
} from "../../services/authService";

import { useAuth } from "../../context/AuthContext";

import "./LoginPage.css";

const STEP = {
  MOBILE: "mobile",
  OTP: "otp",
  PASSWORD: "password",
  RECOVERY: "recovery",
};

const OTP_COOLDOWN_KEY = "login_otp_resend_until";
const RECOVERY_COOLDOWN_KEY = "recovery_otp_resend_until";

const OTP_RESEND_SECONDS = 90;

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const [step, setStep] = useState(STEP.MOBILE);

  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");

  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [recoveryOtp, setRecoveryOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");

  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);

  const [otpSeconds, setOtpSeconds] = useState(0);
  const [recoverySeconds, setRecoverySeconds] = useState(0);

  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);

  const [error, setError] = useState("");

  const otpInputRef = useRef(null);
  const verifyingRef = useRef(false);

  /*
   * Where should the user go after login?
   */
  const redirectTo =
    location.state?.from ||
    "/";

  /*
   * ---------------------------------------------------------
   * Helpers
   * ---------------------------------------------------------
   */

  const normalizeMobile = (value) =>
    value.replace(/\D/g, "").slice(0, 11);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remaining = seconds % 60;

    return `${String(minutes).padStart(2, "0")}:${String(
      remaining
    ).padStart(2, "0")}`;
  };

  const getCooldown = (key) => {
    const until = Number(
      localStorage.getItem(key) || 0
    );

    if (!until) {
      return 0;
    }

    const remaining = Math.ceil(
      (until - Date.now()) / 1000
    );

    if (remaining <= 0) {
      localStorage.removeItem(key);
      return 0;
    }

    return remaining;
  };

  const startCooldown = (key, seconds, setter) => {
    const value = Number(seconds);

    if (!value || value <= 0) {
      return;
    }

    localStorage.setItem(
      key,
      String(Date.now() + value * 1000)
    );

    setter(value);
  };

  /*
   * ---------------------------------------------------------
   * Restore timers
   * ---------------------------------------------------------
   */

  useEffect(() => {
    setOtpSeconds(
      getCooldown(OTP_COOLDOWN_KEY)
    );

    setRecoverySeconds(
      getCooldown(RECOVERY_COOLDOWN_KEY)
    );
  }, []);

  /*
   * One timer for the login OTP.
   */
  useEffect(() => {
    if (otpSeconds <= 0) {
      return;
    }

    const timer = setInterval(() => {
      setOtpSeconds(() => {
        const remaining =
          getCooldown(OTP_COOLDOWN_KEY);

        return remaining;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [otpSeconds]);

  /*
   * One timer for recovery OTP.
   */
  useEffect(() => {
    if (recoverySeconds <= 0) {
      return;
    }

    const timer = setInterval(() => {
      setRecoverySeconds(() => {
        return getCooldown(
          RECOVERY_COOLDOWN_KEY
        );
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [recoverySeconds]);

  /*
   * Focus OTP input.
   */
  useEffect(() => {
    if (step !== STEP.OTP) {
      return;
    }

    const timer = setTimeout(() => {
      otpInputRef.current?.focus();
    }, 100);

    return () => clearTimeout(timer);
  }, [step]);

  /*
   * ---------------------------------------------------------
   * Successful login
   * ---------------------------------------------------------
   */

  const completeLogin = (result) => {
    login(
      result.user,
      result.token
    );

    navigate(
      redirectTo,
      { replace: true }
    );
  };

  /*
   * ---------------------------------------------------------
   * Send login OTP
   * ---------------------------------------------------------
   */

  const handleSendOtp = async (event) => {
    event.preventDefault();

    if (!/^09\d{9}$/.test(mobile)) {
      setError(
        "لطفاً شماره موبایل معتبر وارد کنید."
      );
      return;
    }

    if (otpSeconds > 0) {
      return;
    }

    try {
      setLoading(true);
      setError("");

      const result = await sendOtp(mobile);

      if (!result.success) {
        const remaining = Number(
          result.remaining_seconds || 0
        );

        if (remaining > 0) {
          startCooldown(
            OTP_COOLDOWN_KEY,
            remaining,
            setOtpSeconds
          );
        }

        setError(
          result.message ||
          "لطفاً کمی صبر کنید."
        );

        return;
      }

      startCooldown(
        OTP_COOLDOWN_KEY,
        result.resend_after ||
          OTP_RESEND_SECONDS,
        setOtpSeconds
      );

      setOtp("");

      setStep(STEP.OTP);
    } catch (error) {
      console.error(
        "LOGIN OTP ERROR:",
        error
      );

      const data =
        error.response?.data;

      const remaining = Number(
        data?.remaining_seconds || 0
      );

      if (remaining > 0) {
        startCooldown(
          OTP_COOLDOWN_KEY,
          remaining,
          setOtpSeconds
        );
      }

      setError(
        data?.message ||
        "ارسال کد تایید با خطا مواجه شد."
      );
    } finally {
      setLoading(false);
    }
  };

  /*
   * ---------------------------------------------------------
   * Verify OTP
   * ---------------------------------------------------------
   */

  const verifyLoginOtp = async (value) => {
    if (
      value.length !== 6 ||
      verifyingRef.current
    ) {
      return;
    }

    verifyingRef.current = true;

    try {
      setLoading(true);
      setError("");

      const result =
        await verifyOtp(
          mobile,
          value
        );

      if (!result.success) {
        setError(
          result.message ||
          "کد تایید صحیح نیست."
        );

        return;
      }

      completeLogin(result);
    } catch (error) {
      console.error(
        "VERIFY OTP ERROR:",
        error
      );

      setError(
        error.response?.data?.message ||
        "کد تایید صحیح نیست."
      );
    } finally {
      verifyingRef.current = false;
      setLoading(false);
    }
  };

  const handleOtpChange = (event) => {
    const value =
      event.target.value
        .replace(/\D/g, "")
        .slice(0, 6);

    setOtp(value);
    setError("");

    if (value.length === 6) {
      verifyLoginOtp(value);
    }
  };

  /*
   * ---------------------------------------------------------
   * Resend login OTP
   * ---------------------------------------------------------
   */

  const handleResendOtp = async () => {
    if (
      otpSeconds > 0 ||
      resending
    ) {
      return;
    }

    try {
      setResending(true);
      setError("");

      const result =
        await sendOtp(mobile);

      if (!result.success) {
        const remaining = Number(
          result.remaining_seconds || 0
        );

        if (remaining > 0) {
          startCooldown(
            OTP_COOLDOWN_KEY,
            remaining,
            setOtpSeconds
          );
        }

        setError(
          result.message ||
          "ارسال مجدد کد انجام نشد."
        );

        return;
      }

      startCooldown(
        OTP_COOLDOWN_KEY,
        result.resend_after ||
          OTP_RESEND_SECONDS,
        setOtpSeconds
      );

      setOtp("");
    } catch (error) {
      const data =
        error.response?.data;

      const remaining = Number(
        data?.remaining_seconds || 0
      );

      if (remaining > 0) {
        startCooldown(
          OTP_COOLDOWN_KEY,
          remaining,
          setOtpSeconds
        );
      }

      setError(
        data?.message ||
        "ارسال مجدد کد انجام نشد."
      );
    } finally {
      setResending(false);
    }
  };

  /*
   * ---------------------------------------------------------
   * Password login
   * ---------------------------------------------------------
   */

  const handlePasswordLogin = async (event) => {
    event.preventDefault();

    if (!password) {
      setError(
        "لطفاً رمز عبور را وارد کنید."
      );
      return;
    }

    try {
      setLoading(true);
      setError("");

      const result =
        await loginWithPassword(
          mobile,
          password
        );

      if (!result.success) {
        setError(
          result.message ||
          "شماره موبایل یا رمز عبور اشتباه است."
        );

        return;
      }

      completeLogin(result);
    } catch (error) {
      setError(
        error.response?.data?.message ||
        "ورود انجام نشد."
      );
    } finally {
      setLoading(false);
    }
  };

  /*
   * ---------------------------------------------------------
   * Start password recovery
   * ---------------------------------------------------------
   */

  const handleStartRecovery = async () => {
    if (!/^09\d{9}$/.test(mobile)) {
      setError(
        "لطفاً ابتدا شماره موبایل معتبر وارد کنید."
      );
      return;
    }

    try {
      setLoading(true);
      setError("");

      const result =
        await sendPasswordRecoveryOtp(
          mobile
        );

      if (!result.success) {
        const remaining = Number(
          result.remaining_seconds || 0
        );

        if (remaining > 0) {
          startCooldown(
            RECOVERY_COOLDOWN_KEY,
            remaining,
            setRecoverySeconds
          );
        }

        setError(
          result.message ||
          "ارسال کد بازیابی انجام نشد."
        );

        return;
      }

      setRecoveryOtp("");
      setNewPassword("");
      setRepeatPassword("");

      startCooldown(
        RECOVERY_COOLDOWN_KEY,
        result.resend_after ||
          OTP_RESEND_SECONDS,
        setRecoverySeconds
      );

      setStep(STEP.RECOVERY);
    } catch (error) {
      const data =
        error.response?.data;

      const remaining = Number(
        data?.remaining_seconds || 0
      );

      if (remaining > 0) {
        startCooldown(
          RECOVERY_COOLDOWN_KEY,
          remaining,
          setRecoverySeconds
        );
      }

      setError(
        data?.message ||
        "ارسال کد بازیابی انجام نشد."
      );
    } finally {
      setLoading(false);
    }
  };

  /*
   * ---------------------------------------------------------
   * Reset password
   * ---------------------------------------------------------
   */

  const handleResetPassword = async (event) => {
    event.preventDefault();

    if (recoveryOtp.length !== 6) {
      setError(
        "لطفاً کد تایید ۶ رقمی را وارد کنید."
      );
      return;
    }

    if (newPassword.length < 6) {
      setError(
        "رمز عبور باید حداقل ۶ کاراکتر باشد."
      );
      return;
    }

    if (
      newPassword !==
      repeatPassword
    ) {
      setError(
        "تکرار رمز عبور یکسان نیست."
      );
      return;
    }

    try {
      setLoading(true);
      setError("");

      const result =
        await resetPassword(
          mobile,
          recoveryOtp,
          newPassword,
          repeatPassword
        );

      if (!result.success) {
        setError(
          result.message ||
          "تغییر رمز عبور انجام نشد."
        );
        return;
      }

      setPassword("");
      setRecoveryOtp("");
      setNewPassword("");
      setRepeatPassword("");

      setStep(STEP.PASSWORD);

      setError(
        "رمز عبور با موفقیت تغییر کرد."
      );
    } catch (error) {
      setError(
        error.response?.data?.message ||
        "تغییر رمز عبور انجام نشد."
      );
    } finally {
      setLoading(false);
    }
  };

  /*
   * ---------------------------------------------------------
   * Render
   * ---------------------------------------------------------
   */

  return (
    <main
      className="login-page"
      dir="rtl"
    >
      <section className="login-page__card">

        <header className="login-page__header">
          <div className="login-page__logo">
            {/* Your logo */}
            <span>کافه دارو</span>
          </div>

          <h1>
            {step === STEP.RECOVERY
              ? "بازیابی رمز عبور"
              : "ورود به حساب کاربری"}
          </h1>

          <p>
            {step === STEP.MOBILE &&
              "برای ورود یا ثبت‌نام شماره موبایل خود را وارد کنید."}

            {step === STEP.OTP &&
              `کد ارسال شده به ${mobile} را وارد کنید.`}

            {step === STEP.PASSWORD &&
              `رمز عبور ${mobile} را وارد کنید.`}

            {step === STEP.RECOVERY &&
              "کد بازیابی و رمز عبور جدید خود را وارد کنید."}
          </p>
        </header>

        {step === STEP.MOBILE && (
          <form
            onSubmit={handleSendOtp}
            className="login-page__form"
          >
            <label>
              شماره موبایل
            </label>

            <input
              type="tel"
              inputMode="numeric"
              dir="ltr"
              value={mobile}
              maxLength={11}
              placeholder="09*********"
              autoFocus
              onChange={(e) => {
                setMobile(
                  normalizeMobile(
                    e.target.value
                  )
                );
                setError("");
              }}
            />

            {error && (
              <div className="login-page__error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
            >
              {loading
                ? "در حال ارسال..."
                : "ادامه"}
            </button>

            <div className="login-page__terms">
              با ورود یا ثبت‌نام، شرایط استفاده
              و حریم خصوصی را می‌پذیرید.
            </div>
          </form>
        )}

        {step === STEP.OTP && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              verifyLoginOtp(otp);
            }}
            className="login-page__form"
          >
            <label>
              کد یکبار مصرف
            </label>

            <input
              ref={otpInputRef}
              type="tel"
              inputMode="numeric"
              dir="ltr"
              value={otp}
              maxLength={6}
              placeholder="------"
              autoComplete="one-time-code"
              onChange={handleOtpChange}
            />

            {loading && (
              <div className="login-page__status">
                در حال بررسی کد...
              </div>
            )}

            {error && (
              <div className="login-page__error">
                {error}
              </div>
            )}

            <div className="login-page__resend">
              {otpSeconds > 0 ? (
                <>
                  ارسال مجدد کد تا{" "}
                  <strong>
                    {formatTime(otpSeconds)}
                  </strong>
                </>
              ) : (
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={resending}
                >
                  {resending
                    ? "در حال ارسال..."
                    : "ارسال مجدد کد"}
                </button>
              )}
            </div>

            <button
              type="submit"
              disabled={
                loading ||
                otp.length !== 6
              }
            >
              ورود
            </button>

            <button
              type="button"
              className="login-page__secondary"
              onClick={() => {
                setStep(STEP.PASSWORD);
                setError("");
              }}
            >
              ورود با رمز عبور
            </button>

            <button
              type="button"
              className="login-page__back"
              onClick={() => {
                setStep(STEP.MOBILE);
                setOtp("");
                setError("");
              }}
            >
              تغییر شماره موبایل
            </button>
          </form>
        )}

        {step === STEP.PASSWORD && (
          <form
            onSubmit={handlePasswordLogin}
            className="login-page__form"
          >
            <label>
              رمز عبور
            </label>

            <div className="login-page__password">
              <input
                type={
                  showPassword
                    ? "text"
                    : "password"
                }
                dir="ltr"
                value={password}
                autoFocus
                autoComplete="current-password"
                onChange={(e) => {
                  setPassword(
                    e.target.value
                  );
                  setError("");
                }}
              />

              <button
                type="button"
                onClick={() =>
                  setShowPassword(
                    (value) => !value
                  )
                }
              >
                {showPassword
                  ? "مخفی"
                  : "نمایش"}
              </button>
            </div>

            {error && (
              <div className="login-page__error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
            >
              {loading
                ? "در حال ورود..."
                : "ورود"}
            </button>

            <button
              type="button"
              className="login-page__secondary"
              onClick={handleStartRecovery}
            >
              فراموشی رمز عبور
            </button>

            <button
              type="button"
              className="login-page__back"
              onClick={() => {
                setStep(STEP.OTP);
                setError("");
              }}
            >
              ورود با کد یکبار مصرف
            </button>
          </form>
        )}

        {step === STEP.RECOVERY && (
          <form
            onSubmit={handleResetPassword}
            className="login-page__form"
          >
            <label>
              کد بازیابی
            </label>

            <input
              type="tel"
              inputMode="numeric"
              dir="ltr"
              maxLength={6}
              value={recoveryOtp}
              placeholder="------"
              autoComplete="one-time-code"
              onChange={(e) => {
                setRecoveryOtp(
                  e.target.value
                    .replace(/\D/g, "")
                    .slice(0, 6)
                );
                setError("");
              }}
            />

            <label>
              رمز عبور جدید
            </label>

            <div className="login-page__password">
              <input
                type={
                  showNewPassword
                    ? "text"
                    : "password"
                }
                dir="ltr"
                value={newPassword}
                autoComplete="new-password"
                onChange={(e) => {
                  setNewPassword(
                    e.target.value
                  );
                  setError("");
                }}
              />

              <button
                type="button"
                onClick={() =>
                  setShowNewPassword(
                    (value) => !value
                  )
                }
              >
                {showNewPassword
                  ? "مخفی"
                  : "نمایش"}
              </button>
            </div>

            <label>
              تکرار رمز عبور
            </label>

            <div className="login-page__password">
              <input
                type={
                  showRepeatPassword
                    ? "text"
                    : "password"
                }
                dir="ltr"
                value={repeatPassword}
                autoComplete="new-password"
                onChange={(e) => {
                  setRepeatPassword(
                    e.target.value
                  );
                  setError("");
                }}
              />

              <button
                type="button"
                onClick={() =>
                  setShowRepeatPassword(
                    (value) => !value
                  )
                }
              >
                {showRepeatPassword
                  ? "مخفی"
                  : "نمایش"}
              </button>
            </div>

            {error && (
              <div className="login-page__error">
                {error}
              </div>
            )}

            <div className="login-page__resend">
              {recoverySeconds > 0 ? (
                <>
                  ارسال مجدد کد تا{" "}
                  <strong>
                    {formatTime(
                      recoverySeconds
                    )}
                  </strong>
                </>
              ) : (
                <button
                  type="button"
                  onClick={
                    handleStartRecovery
                  }
                >
                  ارسال مجدد کد
                </button>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
            >
              {loading
                ? "در حال تغییر..."
                : "تغییر رمز عبور"}
            </button>

            <button
              type="button"
              className="login-page__back"
              onClick={() => {
                setStep(
                  STEP.PASSWORD
                );
                setError("");
              }}
            >
              بازگشت
            </button>
          </form>
        )}

      </section>
    </main>
  );
}